import { NotificationType } from '../components/ui/Notification';

export interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
}

class NotificationService {
  private listeners: ((notifications: NotificationItem[]) => void)[] = [];
  private notifications: NotificationItem[] = [];
  private nextId = 1;

  // Suscribirse a los cambios en las notificaciones
  subscribe(listener: (notifications: NotificationItem[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Notificar a todos los listeners sobre los cambios
  private notifyListeners() {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }

  // Agregar una nueva notificación
  addNotification(type: NotificationType, title: string, message?: string, duration?: number) {
    const notification: NotificationItem = {
      id: `notification-${this.nextId++}`,
      type,
      title,
      message,
      duration
    };

    this.notifications.push(notification);
    this.notifyListeners();

    // Auto eliminar después del tiempo especificado (si no se especifica, usar 5 segundos por defecto)
    const autoRemoveDuration = duration || 5000;
    setTimeout(() => {
      this.removeNotification(notification.id);
    }, autoRemoveDuration);

    return notification.id;
  }

  // Eliminar una notificación específica
  removeNotification(id: string) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notifyListeners();
  }

  // Eliminar todas las notificaciones
  clearAllNotifications() {
    this.notifications = [];
    this.notifyListeners();
  }

  // Métodos de conveniencia para diferentes tipos de notificaciones
  success(title: string, message?: string, duration?: number) {
    return this.addNotification('success', title, message, duration);
  }

  error(title: string, message?: string, duration?: number) {
    return this.addNotification('error', title, message, duration);
  }

  warning(title: string, message?: string, duration?: number) {
    return this.addNotification('warning', title, message, duration);
  }

  info(title: string, message?: string, duration?: number) {
    return this.addNotification('info', title, message, duration);
  }

  // Obtener todas las notificaciones actuales
  getNotifications(): NotificationItem[] {
    return [...this.notifications];
  }
}

// Exportar una instancia única del servicio
export const notificationService = new NotificationService();
export default notificationService;
export {};